import { useEffect, useRef } from "react";

import { axios } from "../libs/axios";
import { refreshToken } from "../services/auth/refresh";
import { useAuthStore } from "../stores/auth";

type Props = {
  children: React.ReactNode;
};

/**
 * The AuthRefreshProvider wrapper is responsible for refreshing
 * the access token every 5 minutes while the user is authenticated.
 *
 * @param children The children to render.
 */
export const AuthRefreshProvider = ({ children }: Props) => {
  const intervalId = useRef<NodeJS.Timeout>();
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { user, subscriptionTier } = useAuthStore();
  const syncSubscriptionTier = useAuthStore((state) => state.syncSubscriptionTier);

  // Sync subscription on mount and when user changes
  useEffect(() => {
    // First sync the subscription tier from localStorage
    syncSubscriptionTier();

    // Log user authentication state when it changes
    if (isLoggedIn) {
    } else {
    }
  }, [isLoggedIn, user, syncSubscriptionTier, subscriptionTier]);

  // Handle token refresh
  useEffect(() => {
    if (!isLoggedIn && intervalId.current) {
      clearInterval(intervalId.current);
      return;
    }

    const _refreshToken = async () => {
      try {
        await refreshToken(axios);
        // Sync subscription tier after token refresh
        syncSubscriptionTier();
      } catch {
        // Token refresh failed - the axios interceptor handles this
        // by invalidating the user session. No need to do anything here.
        console.warn("[AuthRefreshProvider] Token refresh failed, session may have expired");
      }
    };

    intervalId.current = setInterval(_refreshToken, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId.current);
    };
  }, [isLoggedIn, syncSubscriptionTier]);

  return children;
};
